/* ============================================================
   playlist-shape.js — the playlist share page's data, as a pure function so node can test it (scripts/room.test.mjs).

   fromWire(d, id) → { id, title, intent, cover, is_owner, songs: [{ id, title, dur, cover, audio_url, lrc }] }

   The wire is what we asked mia-data for (docs/API-playlists.md §share): GET /share/playlists/{id}, the songs RESOLVED
   inside the answer — a stranger holds no bearer, so the room's two doors (/client/songs/batch, /audio-urls) are shut
   to them. Field names follow the §4.5 playlist rows (name / description / cover_image) and the catalog song
   (title / duration_ms / cover_image / lyrics_lrc), with the page's own names accepted too, so a backend that copies the
   page shape verbatim is not wrong either. A song with audio_url null is KEPT: it shows on the sheet, it does not play
   (the endpoint decides what a stranger may hear — a catalog record the label has not cleared, say).
   ============================================================ */
const isUrl = (u) => /^https?:\/\//i.test(u || '') || /^\//.test(u || '');
const str = (v) => (v == null ? '' : String(v));

export function songFromWire(s, i = 0) {
  const x = s && s.song ? { ...s.song, song_id: s.song_id } : s || {}; /* a batch row { song_id, song } is fine too */
  const dur = Math.round(Number(x.duration_ms) / 1000) || Number(x.duration_sec) || Number(x.dur) || 0;
  return {
    id: str(x.song_id ?? x.id ?? `s${i}`),
    title: str(x.title).trim() || 'Untitled',
    dur /* seconds; 0 = unknown until the file's metadata says */,
    cover: isUrl(x.cover_image) ? x.cover_image : isUrl(x.cover) ? x.cover : '',
    audio_url: str(x.audio_url || x.audioUrl || x.url) /* '' = not for a stranger's ears (or no asset) */,
    lrc: str(x.lyrics_lrc || x.lrc),
  };
}

export function fromWire(d, id = '') {
  d = d || {};
  const songs = (Array.isArray(d.songs) ? d.songs : Array.isArray(d.items) ? d.items : []).map(songFromWire);
  return {
    id: str(d.id ?? id),
    title: str(d.name ?? d.title).trim(),
    intent: str(d.description ?? d.intent).trim(),
    cover: isUrl(d.cover_image) ? d.cover_image : isUrl(d.cover) ? d.cover : songs.find((s) => s.cover)?.cover || '',
    is_owner: !!(d.is_owner ?? d.isOwner),
    songs,
  };
}

/* the who-line's numbers: "8 songs · 24 min" — minutes only, a playlist is not a stopwatch */
export function sumLine(songs) {
  const n = songs.length;
  const sec = songs.reduce((a, s) => a + (s.dur || 0), 0);
  const min = Math.round(sec / 60);
  return `${n} ${n === 1 ? 'song' : 'songs'}${min ? ` · ${min} min` : ''}`;
}
